import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ActionsApiService } from '../../action-lists/actions';
import { MarketsService } from '../../core/markets/markets.service';
import { PartnerService } from '../../core/partner.service';

const PAGE_SIZE = 20;
const DEFAULT_ACCOUNT_SUPPORT_URL = 'https://docs.vendasta.com/docs/marketing/campaigns/email-campaigns-recipients';

@Component({
  templateUrl: './account-selector-dialog.component.html',
  styleUrls: ['./account-selector-dialog.component.scss'],
  standalone: false,
})
export class AccountSelectorDialogComponent implements OnInit, OnDestroy {
  @Input()
  partnerId: string;
  @Input()
  userListId: string;
  @Input()
  userListName: string;
  @Input()
  selectedCampaignId: string;
  @Input()
  campaignStart: any;
  @Input()
  selectedCampaignTimezome: any;
  @Input()
  fromEmail: string;
  @Input()
  fromName: string;
  @Input()
  useCustomEmail: boolean;

  private accessibleMarkets: string[];
  accountGroups$: Observable<AccountGroup[]>;
  searchTerm$$: BehaviorSubject<string> = new BehaviorSubject('');
  loading$: Observable<boolean> = this.accountGroupService.loading;
  subscriptions: Subscription[] = [];
  accountGroupSearch = new UntypedFormControl();
  useDefaultAccountGroup = true;
  hasMore$: Observable<boolean> = this.accountGroupService.hasMore;
  selectedAccountGroup: any;
  defaultAccountUrl = DEFAULT_ACCOUNT_SUPPORT_URL;

  constructor(
    public dialogRef: MatDialogRef<AccountSelectorDialogComponent>,
    private partnerService: PartnerService,
    private actionsService: ActionsApiService,
    private accountGroupService: AccountGroupService,
    private marketsService: MarketsService,
  ) {}

  ngOnInit(): void {
    this.accountGroups$ = this.accountGroupService.accountGroups;
    this.subscriptions.push(
      this.marketsService.markets
        .pipe(
          map((markets) => {
            this.accessibleMarkets = markets.map((market) => market.market_id);
          }),
        )
        .subscribe(),
    );
    this.accountGroupService
      .lookup(
        new ProjectionFilter({
          napData: true,
        }),
        this.accessibleMarkets,
        '',
        undefined,
        PAGE_SIZE,
      )
      .pipe(take(1));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  updateSearchTerm(event: Event): void {
    this.selectedAccountGroup = null;
    const searchValue = (event.target as HTMLInputElement).value;
    this.searchTerm$$.next(searchValue);
    this.search(searchValue);
  }

  displayFn(accountGroup?: AccountGroup): string | undefined {
    return accountGroup ? accountGroup.napData.companyName : undefined;
  }

  selectAccount(accountGroup: AccountGroup): void {
    if (!accountGroup) {
      this.accountGroupSearch.setValue(null);
      this.selectedAccountGroup = null;
      return;
    }
    this.selectedAccountGroup = accountGroup;
  }

  close(_accountGroup: AccountGroup): void {
    this.dialogRef.close(null);
  }

  search(searchValue: string): void {
    this.accountGroupService
      .lookup(
        new ProjectionFilter({
          napData: true,
        }),
        this.accessibleMarkets,
        searchValue,
        undefined,
        PAGE_SIZE,
      )
      .pipe(take(1));
  }

  public loadMore(): void {
    this.accountGroupService
      .loadMore(
        new ProjectionFilter({
          napData: true,
        }),
        this.accessibleMarkets,
        this.searchTerm$$.getValue(),
        undefined,
        PAGE_SIZE,
      )
      .pipe(take(1));
  }

  startCampaign(): void {
    const accountGroupId = this.useDefaultAccountGroup ? this.selectedAccountGroup.accountGroupId : null;
    this.actionsService
      .scheduleCampaignForUserList(
        this.partnerId,
        this.userListId,
        this.selectedCampaignId,
        this.campaignStart,
        this.selectedCampaignTimezome,
        accountGroupId,
        this.fromEmail,
        this.fromName,
      )
      .subscribe((resp) => {
        return this.dialogRef.close(resp);
      });
  }

  defaultAccountLearnMore(): void {
    window.open(DEFAULT_ACCOUNT_SUPPORT_URL, '_blank', 'noopener');
  }
}
